import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, ArrowRight, PhoneCall, Zap, Leaf } from 'lucide-react';
import { useSection } from '../../hooks/useSection';

const DEFAULT_BADGES = ['100% électrique', 'Zéro émission', 'Made in Senegal'];

const FinalCTA: React.FC = () => {
  const { section } = useSection('home_final_cta');
  if (section && section.is_visible === false) return null;

  const eyebrow = section?.subtitle || "L'avenir de la mobilité";
  const paragraph = section?.content || 'Motos, stations de recharge, financement adapté. Nos experts vous accompagnent dans votre transition.';
  const cta1Label = section?.button_label || 'Voir nos motos';
  const cta1Link = section?.button_url || '/products';
  const cta2 = section?.extra_data?.cta_secondary || { label: 'Nous contacter', link: '/contact' };
  const badges: string[] = section?.extra_data?.badges?.length ? section.extra_data.badges : DEFAULT_BADGES;
  const badgeIcons = [Zap, Leaf, Sparkles];

  return (
    <section className="relative overflow-hidden py-16 sm:py-20 lg:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative overflow-hidden rounded-[2rem] bg-neutral-950">
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute -top-32 right-[10%] h-96 w-96 rounded-full bg-primary-500/10 blur-3xl" />
            <div className="absolute -bottom-20 left-[15%] h-72 w-72 rounded-full bg-accent-300/[0.06] blur-3xl" />
            <svg className="absolute inset-0 h-full w-full opacity-[0.03]" viewBox="0 0 1600 600" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
              <defs>
                <pattern id="cta-grid" width="60" height="60" patternUnits="userSpaceOnUse">
                  <path d="M 60 0 L 0 0 0 60" fill="none" stroke="white" strokeWidth="0.5" />
                </pattern>
              </defs>
              <rect width="1600" height="600" fill="url(#cta-grid)" />
            </svg>
          </div>
          <div className="relative px-6 py-14 sm:px-12 sm:py-20 lg:px-16 lg:py-24">
            <div className="mx-auto max-w-3xl text-center">
              <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 backdrop-blur-sm">
                <Sparkles className="h-3.5 w-3.5 text-primary-400" strokeWidth={2.5} />
                <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-white/80">{eyebrow}</span>
              </div>
              <h2 className="mb-6 text-3xl font-black leading-[1.05] tracking-[-0.025em] text-white sm:text-4xl lg:text-5xl xl:text-6xl">
                {section?.title ? section.title : (<>Passez à la mobilité <span className="block text-primary-400">électrique.</span></>)}
              </h2>
              <p className="mx-auto mb-10 max-w-xl text-base leading-relaxed text-white/70 sm:text-lg">
                {paragraph}
              </p>
              <div className="flex flex-col items-stretch justify-center gap-3 sm:flex-row">
                <Link
                  to={cta1Link}
                  className="group inline-flex items-center justify-center gap-2 rounded-xl bg-white px-7 py-3.5 text-sm font-bold text-neutral-900 shadow-[0_18px_40px_-12px_rgba(255,255,255,0.4)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-primary-400 hover:shadow-[0_24px_50px_-12px_rgba(74,222,128,0.5)] sm:text-base"
                >
                  {cta1Label}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Link>
                <Link
                  to={cta2.link || '/contact'}
                  className="group inline-flex items-center justify-center gap-2 rounded-xl border border-white/20 bg-white/5 px-7 py-3.5 text-sm font-bold text-white backdrop-blur-sm transition-all duration-300 hover:-translate-y-0.5 hover:border-white/40 hover:bg-white/10 sm:text-base"
                >
                  <PhoneCall className="h-4 w-4" />
                  {cta2.label || 'Nous contacter'}
                </Link>
              </div>
              <div className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 border-t border-white/10 pt-8">
                {badges.map((b, i) => {
                  const Icon = badgeIcons[i % badgeIcons.length];
                  return (
                    <div key={i} className="flex items-center gap-2 text-white/60">
                      <Icon className="h-4 w-4 text-primary-400" strokeWidth={2.5} />
                      <span className="text-xs font-medium">{b}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FinalCTA;
